import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
  TextareaAutosize,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Switch } from '@headlessui/react';
import warningIcon from '../../../assets/svgs/warning.svg';
import SelectDropdown from '../../../components/SelectDropdown/SelectDropdown';

interface ResendConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, shouldRemakeFood: boolean) => void;
}

const ResendConfirmationModal = ({
  open,
  onClose,
  onConfirm,
}: ResendConfirmationModalProps) => {
  const [atFault, setAtFault] = useState('');
  const [resendReason, setResendReason] = useState('');
  const [shouldRemakeFood, setShouldRemakeFood] = useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setAtFault('');
      setResendReason('');
      setShouldRemakeFood(false);
      setHasValidationError(false);
    }
  }, [open]);

  const handleConfirm = () => {
    // Validate that a category is selected
    if (!atFault || atFault === '') {
      setHasValidationError(true);
      return;
    }

    const formattedReason = `Fault: ${atFault}. Note: ${resendReason}. Remake: ${shouldRemakeFood}`;
    onConfirm(formattedReason, shouldRemakeFood);
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  const faultOptions = [
    {
      displayText: 'Select a category',
      value: ''
    },
    {
      displayText: 'Car in Drive',
      value: 'CAR_IN_DRIVE'
    },
    {
      displayText: 'Merchant Issue',
      value: 'MERCHANT_ISSUE'
    },
    {
      displayText: 'Manna Ops Issue',
      value: 'MANNA_ISSUE_OPS'
    },
    {
      displayText: 'Software Issue',
      value: 'SOFTWARE_ISSUE'
    },
    {
      displayText: 'Weather (Wind)',
      value: 'WEATHER_WIND'
    },
    {
      displayText: 'Weather (Rain)',
      value: 'WEATHER_RAIN'
    },
    {
      displayText: 'Bag Drop',
      value: 'BAG_DROP'
    },
    {
      displayText: 'Too long at base',
      value: 'TOO_LONG_AT_BASE'
    },
    {
      displayText: 'Other',
      value: 'OTHER'
    }
  ];

  return (
    <Dialog
      open={open}
        onClose={handleClose}
      aria-labelledby="resend-confirmation-dialog-title"
      PaperProps={{
        style: styles.dialogPaper
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        style={styles.closeButton}
      >
        <CloseIcon />
      </IconButton>
      
      <DialogContent style={styles.dialogContent}>
        <div style={styles.warningIcon}>
          <img src={warningIcon} alt="Warning" style={styles.warningImage} />
        </div>
        
        <Box style={styles.contentContainer}>
          <Typography
            id="resend-confirmation-dialog-title"
            style={styles.title}
          >
            Reset & Resend
          </Typography>
          
          <Typography style={styles.description}>
            Please select the reason for resending this order and provide any additional details.
          </Typography>

          <div style={styles.formContainer}>
            <div style={styles.fieldContainer}>
              <div className={hasValidationError ? 'dropdown-error' : ''}>
                <style>{`
                  .dropdown-error .relative.mt-2 button {
                    border-color: #dc2626 !important;
                    border-width: 2px !important;
                    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2) !important;
                  }
                  .dropdown-placeholder button span:first-child {
                    color: #9ca3af !important;
                    font-style: italic;
                  }
                `}</style>
                <div className={atFault === '' ? 'dropdown-placeholder' : ''}>
                  <SelectDropdown
                    label='Category'
                    value={atFault}
                    onChange={(value: string) => {
                      setAtFault(value);
                      // Clear validation error when user makes a selection
                      if (hasValidationError && value !== '') {
                        setHasValidationError(false);
                      }
                    }}
                    options={faultOptions}
                  />
                </div>
              </div>
            </div>

            <div style={styles.fieldContainer}>
              <Typography style={styles.fieldLabel}>Additional Notes</Typography>
              <TextareaAutosize
                style={styles.textarea}
                maxRows={6}
                minRows={3}
                value={resendReason}
                onChange={(evt) => setResendReason(evt.target.value)}
                placeholder="Enter any additional details..."
              />
            </div>

            <Switch.Group as="div" style={styles.switchContainer}>
              <Switch
                checked={shouldRemakeFood}
                onChange={setShouldRemakeFood}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${shouldRemakeFood ? 'bg-blue-600' : 'bg-gray-200'}`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${shouldRemakeFood ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </Switch>
              <Switch.Label as="span" style={styles.switchLabel}>
                <span style={styles.switchLabelText}>Remake food</span>
              </Switch.Label>
            </Switch.Group>
          </div>
        </Box>
        
        <div style={styles.buttonContainer}>
          <Button
            onClick={handleClose}
            style={styles.cancelButton}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            style={styles.actionButton}
            variant="contained"
          >
            Resend Order
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  dialogPaper: {
    borderRadius: 12,
    padding: 0,
    maxWidth: 500,
    width: '100%',
  },
  dialogContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  closeButton: {
    position: 'absolute' as const,
    right: 24,
    top: 24,
    width: 24,
    height: 24,
    color: '#6b7280',
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    marginBottom: 32,
  },
  warningIcon: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
    flexShrink: 0,
  },
  warningImage: {
    width: 24,
    height: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    color: '#101828',
    fontFamily: 'Outfit',
  },
  description: {
    fontSize: 16,
    color: '#475467',
    fontWeight: 400,
    lineHeight: 1.5,
    fontFamily: 'Outfit',
    marginBottom: 24,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    fontFamily: 'Outfit',
  },
  textarea: {
    padding: 12,
    width: '100%',
    fontFamily: 'Arial',
    fontSize: 14,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    resize: 'vertical' as const,
    outline: 'none',
    '&:focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  switchLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  switchLabelText: {
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    fontFamily: 'Outfit',
  },
  buttonContainer: {
    display: 'flex',
    gap: 12,
    width: '100%',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    width: 170,
    height: 44,
    borderRadius: 8,
    padding: '10px 18px',
    textTransform: 'none' as const,
    fontSize: 16,
    fontWeight: 500,
    color: '#111827',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    fontFamily: 'Outfit',
    '&:hover': {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
    },
  },
  actionButton: {
    width: 170,
    height: 44,
    borderRadius: 8,
    padding: '10px 18px',
    textTransform: 'none' as const,
    fontSize: 16,
    fontWeight: 500,
    color: 'white',
    backgroundColor: '#3b82f6',
    fontFamily: 'Outfit',
    '&:hover': {
      backgroundColor: '#2563eb',
    },
  },
};

export default ResendConfirmationModal;
